/**
 * Note action bar gating for the Encounter screen.
 *
 * A completed (FHIR finished) encounter is read-only. Add Addendum is available
 * on a signed Note only while the encounter is open or reopened.
 */
import { isAmendmentVersionKind, isEncounterReadOnly } from "@/lib/encounterApi";
import { canSignNote, draftVersionKind, noteDisplayStatus, noteIsReadOnly } from "@/lib/noteApi";

/** An addendum or correction draft is open, so writes survive Close Encounter. */
export function hasOpenAmendmentDraft(note) {
  return isAmendmentVersionKind(draftVersionKind(note));
}

/** True when the encounter status alone is what blocks writing this Note. */
export function encounterBlocksNote(encounter, note) {
  return isEncounterReadOnly(encounter) && !hasOpenAmendmentDraft(note);
}

/**
 * Resolve every Note action from encounter status, Note status, and write access.
 * canWrite is the caller's permission check (encounter write + note write).
 */
export function noteActionState({ encounter, note = null, canWrite = true } = {}) {
  const blockedByEncounter = encounterBlocksNote(encounter, note);
  const editorLocked = blockedByEncounter || noteIsReadOnly(note) || !canWrite;
  return {
    blockedByEncounter,
    editorLocked,
    canSave: !editorLocked,
    canSign: !editorLocked && canSignNote(note),
    // Add Addendum needs a signed Note and an open or reopened Encounter.
    canAmend: canWrite && !isEncounterReadOnly(encounter) && noteDisplayStatus(note) === "signed",
    autosaveOff: isEncounterReadOnly(encounter) && editorLocked,
  };
}
